/**
 * GolfAuth wordmark — switches between light and dark image variants.
 *
 * Both apps serve the images from their own /public/ directory:
 *   /nav-logo-light.webp + nav-logo-light-{400,800}.png  (light mode, 800×160)
 *   /nav-logo-dark.webp + hero-logo-{400,800}.png        (dark mode, 800×160)
 *
 * The original assets were 4746×951 (light) and 3559×714 (dark) which
 * Lighthouse flagged as ~50× oversized for the nav's 32-40px display
 * height. The resized variants ship at 800×160 (2x retina at h-20)
 * with WebP fallback to PNG for older Safari/Android.
 *
 * Explicit width/height attributes are intrinsic pixel dimensions —
 * browsers use them to compute aspect-ratio for layout reservation
 * (eliminates the CLS hit). CSS rules in className still control the
 * rendered size (`h-8 w-auto` etc.).
 *
 * `variant`:
 *   'default' (default) — wordmark only. Used on consumer marketing
 *                          pages and golfer-facing routes.
 *   'pro'              — wordmark + small "PRO" badge to the right.
 *                          Used on /pro and all professional / admin /
 *                          fitter routes so the brand identity reflects
 *                          which side of the platform the user is on.
 *
 * `surface`:
 *   'auto'  (default) — follow the document `.dark` class (light asset
 *                        on light chrome, dark asset on dark chrome).
 *   'dark'            — always the dark-surface wordmark (white logo).
 *                        Required on always-dark chrome such as the
 *                        `.mkt nav.bar` sticky marketing nav, which
 *                        stays on a dark gradient even if the document
 *                        theme is light.
 *   'light'           — always the light-surface wordmark (dark logo).
 */

export type GolfAuthNavLogoVariant = 'default' | 'pro';
export type GolfAuthNavLogoSurface = 'auto' | 'dark' | 'light';

export interface GolfAuthNavLogoProps {
  className?: string;
  variant?: GolfAuthNavLogoVariant;
  surface?: GolfAuthNavLogoSurface;
}

function WordmarkImg({
  kind,
  className,
}: {
  kind: 'light' | 'dark';
  className: string;
}) {
  if (kind === 'light') {
    return (
      <picture>
        <source srcSet="/nav-logo-light.webp" type="image/webp" />
        <img
          src="/nav-logo-light-800.png"
          srcSet="/nav-logo-light-400.png 1x, /nav-logo-light-800.png 2x"
          alt=""
          aria-hidden
          width={800}
          height={160}
          className={className}
        />
      </picture>
    );
  }
  return (
    <picture>
      <source srcSet="/nav-logo-dark.webp" type="image/webp" />
      <img
        src="/hero-logo-800.png"
        srcSet="/hero-logo-400.png 1x, /hero-logo-800.png 2x"
        alt=""
        aria-hidden
        width={800}
        height={160}
        className={className}
      />
    </picture>
  );
}

export function GolfAuthNavLogo({
  className,
  variant = 'default',
  surface = 'auto',
}: GolfAuthNavLogoProps) {
  // Size / object-fit MUST land on <img>, not <picture>. Height utilities
  // on <picture> do not shrink the image — the intrinsic 800×160 asset
  // then paints full-width and covers sibling footer copy.
  const sizeCls = className?.trim() || 'h-8 w-auto';
  const imgCls = `${sizeCls} object-contain`.trim();

  const ariaLabel = variant === 'pro' ? 'GolfAuth Pro' : 'GolfAuth';

  return (
    <div
      className="inline-flex shrink-0 items-baseline gap-2"
      role="img"
      aria-label={ariaLabel}
    >
      {surface === 'auto' ? (
        <>
          <picture className="dark:hidden">
            <source srcSet="/nav-logo-light.webp" type="image/webp" />
            <img
              src="/nav-logo-light-800.png"
              srcSet="/nav-logo-light-400.png 1x, /nav-logo-light-800.png 2x"
              alt=""
              aria-hidden
              width={800}
              height={160}
              className={imgCls}
            />
          </picture>
          <picture className="hidden dark:block">
            <source srcSet="/nav-logo-dark.webp" type="image/webp" />
            <img
              src="/hero-logo-800.png"
              srcSet="/hero-logo-400.png 1x, /hero-logo-800.png 2x"
              alt=""
              aria-hidden
              width={800}
              height={160}
              className={imgCls}
            />
          </picture>
        </>
      ) : (
        <WordmarkImg kind={surface === 'dark' ? 'dark' : 'light'} className={imgCls} />
      )}
      {variant === 'pro' && (
        <span
          aria-hidden
          className="
            inline-flex items-center
            font-semibold tracking-wider uppercase
            border rounded px-1.5 py-0.5
            text-[0.55rem] sm:text-[0.6rem] leading-none
          "
          style={{
            color: 'var(--color-brand)',
            borderColor: 'var(--color-brand)',
            // Slight translateY so the badge baseline-aligns with the
            // wordmark across both image height variants.
            transform: 'translateY(-1px)',
          }}
        >
          Pro
        </span>
      )}
    </div>
  );
}
